import { ChevronDown, Menu } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getMyInfo } from "interfaces/user";
import logo from "assets/img/logo.png";
import { DropdownMenu, DropdownItem, DropdownDivider } from "components/ui/DropdownMenu";
import { Avatar } from "views/linkedin/parts";

interface TopbarProps {
    brandText: string;
    onOpenSidenav?: () => void;
}

const Topbar = ({ brandText, onOpenSidenav }: TopbarProps) => {
    const [me, setMe] = useState<any>(null);
    const [showUserMenu, setShowUserMenu] = useState(false);
    const nav = useNavigate();

    useEffect(() => {
        const initMe = async () => {
            try {
                const response = await getMyInfo();
                setMe(response.data);
            } catch (error) {
                console.error("Error fetching user info:", error);
            }
        };
        initMe();
    }, []);

    // "prenom.nom@entreprise.fr" → "Prenom"
    const localPart = me?.email?.split("@")[0]?.split(".")[0] || "";
    const displayName = localPart ? localPart[0].toUpperCase() + localPart.slice(1) : "Utilisateur";

    const handleLogout = () => {
        localStorage.removeItem("token");
        localStorage.removeItem("isAdmin");
        localStorage.removeItem("admin-token");
        nav("/auth/login");
    };

    return (
        // From xl the sidebar rail (24px from the edge, 100px wide) holds the logo, so the bar starts after it.
        <div className="sticky top-0 z-50 px-8 pt-3 pb-5 bg-canvas font-sans xl:pl-[140px]">
            <header className="h-12 bg-surface-inverse flex items-center justify-between px-5 rounded-lg">
                {/* Logo & Mobile Menu & Breadcrumb */}
                <div className="flex items-center gap-10">
                    {/* Mobile menu button */}
                    <button
                        onClick={onOpenSidenav}
                        className="xl:hidden text-ink-inverse-muted hover:text-ink-inverse p-1.5 rounded-md hover:bg-white/10 transition-colors duration-150"
                    >
                        <Menu className="w-5 h-5" />
                    </button>

                    <div
                        className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity duration-150 xl:hidden"
                        onClick={() => nav("/dashboard")}
                    >
                        <img
                            src={logo}
                            alt="Duckflow Logo"
                            className="h-8 w-8 object-contain bg-white/90 rounded-lg p-1"
                        />
                        <span className="font-display text-base font-normal tracking-tight text-ink-inverse">
                            Duckflow
                        </span>
                    </div>

                    {/* Breadcrumb */}
                    <nav className="flex items-center gap-1.5 text-sm text-ink-inverse-muted">
                        <span className="text-ink-inverse font-medium">{brandText || "Dashboard"}</span>
                    </nav>
                </div>

            {/* Right actions */}
            <div className="flex items-center gap-3">
                {/* Token counter (if subscribed) */}
                {me?.isSubscribed && (
                    <div
                        className="bg-white/10 text-ink-inverse text-xs px-2.5 py-1.5 rounded-md cursor-pointer hover:bg-white/20 transition-colors duration-150"
                        onClick={() => nav("/tokens")}
                    >
                        <span className="font-medium">{me.tokens} tokens</span>
                    </div>
                )}

                {/* User menu */}
                <div className="relative">
                    <button
                        className="flex items-center gap-2 text-sm text-ink-inverse cursor-pointer hover:bg-white/10 rounded-lg px-2 py-1.5 transition-colors duration-150"
                        onClick={() => setShowUserMenu(!showUserMenu)}
                        onBlur={() => setTimeout(() => setShowUserMenu(false), 200)}
                    >
                        <Avatar name={displayName} size="sm" />
                        <span className="hidden sm:inline">{displayName}</span>
                        <ChevronDown className="w-3.5 h-3.5 text-ink-inverse-muted" />
                    </button>

                    {/* Dropdown menu */}
                    {showUserMenu && (
                        <DropdownMenu className="absolute right-0 top-full mt-2 w-56 z-50">
                            <div className="px-3 py-2 border-b border-border mb-1">
                                <p className="text-sm font-semibold text-ink truncate">
                                    {me?.email || "Utilisateur"}
                                </p>
                                <p className="text-xs text-ink-muted mt-0.5">
                                    {me?.role === "admin" ? "Administrateur" : "Utilisateur"}
                                </p>
                            </div>
                            <DropdownItem
                                onClick={() => {
                                    setShowUserMenu(false);
                                    nav("/settings");
                                }}
                            >
                                Paramètres
                            </DropdownItem>
                            <DropdownDivider />
                            <DropdownItem destructive onClick={handleLogout}>
                                Déconnexion
                            </DropdownItem>
                        </DropdownMenu>
                    )}
                </div>
            </div>
        </header>
        </div>
    );
};

export default Topbar;
